import { HTMLControl } from './html_control.js';

/**
 * Calendar control.
 */
class CalendarControl extends HTMLControl {
  initialize() {
    super.initialize();
    if (this.hasAttribute('value')) {
      this.set(JSON.parse(this.getAttribute('value')));
    }
  }

  set(data, today = new Date()) {
    // The data format is an array representing months, the current months goes
    // at 0 index; each month is an array objects representing days, having
    // 'avail' property indicating whether a location is available this day.
    this.data = data;
    this.today = today;

    let prevToLastIndex = data.length - 2;
    let index =
      this.#currentIndex > prevToLastIndex
        ? (this.#currentIndex = prevToLastIndex)
        : this.#currentIndex;
    this.setMonth(index);
  }

  setMonth(index) {
    if (this.#currentIndex != index) {
      this.#currentIndex = index;
      this.dispatchEvent(new CustomEvent('monthchange'), {
        detail: index,
      });
    }

    let thismonth =
      index == 0
        ? new Date(this.today)
        : new Date(
            new Date(this.today).setMonth(this.today.getMonth() + index, 1)
          );
    let nextmonth = new Date(
      new Date(this.today).setMonth(this.today.getMonth() + index + 1, 1)
    );

    let markup =
      this.getMonthMarkup(thismonth, index) +
      this.getMonthMarkup(nextmonth, index + 1);

    this.classList.add('calendar-container');
    this.innerHTML = markup;

    this.rightArrowElements.forEach(rightArrow =>
      rightArrow.addEventListener('click', () =>
        this.setMonth(this.#currentIndex + 1)
      )
    );
    this.leftArrowElements.forEach(leftArrow =>
      leftArrow.addEventListener('click', () =>
        this.setMonth(this.#currentIndex - 1)
      )
    );
  }

  // eslint-disable-next-line complexity
  getMonthMarkup(date, index) {
    const days = this.data[index];
    const pdays = this.data[index - 1];
    const ndays = this.data[index + 1];

    const name = date.toLocaleString('en-us', { month: 'long' });
    let headerMarkup = this.getMonthHeaderMarkup({
      name,
      isLeftDisabled: !pdays,
      isRightDisabled: !ndays,
    });
    let markup = `
      <tr>
        <th colspan='7'>${headerMarkup}</th>
      </tr>
    `;

    const wd = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    markup += `<tr>${wd.map(i => `<th>${i}</th>`).join('')}</tr>`;

    let fd = new Date(new Date(date).setDate(1)).getDay();
    let lpd = new Date(new Date(date).setDate(0)).getDate();

    // First row: previous or this month days.
    markup += '<tr>';
    for (let i = 0; i < 7; i++) {
      let d = ((lpd - fd + i) % lpd) + 1;
      let cn = '';
      if (i < fd) {
        // prev month
        if (index > 0) {
          cn = pdays?.[d - 1]
            ? pdays[d - 1].avail
              ? 'available'
              : 'unavailable'
            : '';
        }
        cn += ' calendar-prevmonth-day';
      } else if (index > 0 || d >= date.getDate()) {
        cn = days?.[d - 1]
          ? days[d - 1].avail
            ? 'available'
            : 'unavailable'
          : '';
      }
      markup += `<td class='${cn}'>${d}</td>`;
    }
    markup += '</tr>';

    // Middle rows.
    let cd = 8 - fd;
    for (let week = 1; week < 4; week++) {
      markup += '<tr>';
      for (let day = 0; day < 7; day++) {
        let cn = '';
        if (index > 0 || cd >= date.getDate()) {
          cn = days?.[cd - 1]
            ? days[cd - 1].avail
              ? 'available'
              : 'unavailable'
            : '';
        }
        markup += `<td class='${cn}'>${cd++}</td>`;
      }
      markup += '</tr>';
    }

    // Last two rows: this or next month days.
    let ld = new Date(
      new Date(
        new Date(date).setMonth(new Date(date).getMonth() + 1, 1)
      ).setDate(0)
    ).getDate();

    for (let row = 0; row <= 1; row++) {
      markup += '<tr>';
      for (let i = 0; i < 7; i++) {
        let dayindex = i + row * 7;
        let d = ((cd + dayindex - 1) % ld) + 1;
        let cn = '';
        if (cd + dayindex - 1 < ld) {
          // this month
          if (index > 0 || d >= date.getDate()) {
            cn = days?.[d - 1]
              ? days[d - 1].avail
                ? 'available'
                : 'unavailable'
              : '';
          }
        } else if (ndays) {
          cn = ndays?.[d - 1]
            ? ndays[d - 1].avail
              ? 'available'
              : 'unavailable'
            : '';
          cn += ' calendar-nextmonth-day';
        }
        markup += `<td class='${cn}'>${d}</td>`;
      }
      markup += '</tr>';
    }

    return `<div class='calendar-month'><table>${markup}</table></div>`;
  }

  getMonthHeaderMarkup({ name, isLeftDisabled, isRightDisabled }) {
    let leftArrowClass = (isLeftDisabled && 'month-arrow-disabled') || '';
    let rightArrowClass = (isRightDisabled && 'month-arrow-disabled') || '';
    return `
      <div class='month-header'>
        <div class='month-arrow month-left-arrow ${leftArrowClass}'><svg viewBox='0 0 18 18'>
          <path d='m13.7 16.29a1 1 0 1 1 -1.42 1.41l-8-8a1 1 0 0 1 0-1.41l8-8a1 1 0 1 1 1.42 1.41l-7.29 7.29z'
          fill-rule='evenodd'></path>
        </svg></div>
        <div class='month-header-name'>${name}</div>
        <div class='month-arrow month-right-arrow ${rightArrowClass}'><svg viewBox='0 0 18 18'>
          <path d="m4.29 1.71a1 1 0 1 1 1.42-1.41l8 8a1 1 0 0 1 0 1.41l-8 8a1 1 0 1 1 -1.42-1.41l7.29-7.29z" fill-rule="evenodd"></path>
        </svg></div>
      </div>
    `;
  }

  static get selectors() {
    const LeftCalendar = '.calendar-month:first-child';
    const RightCalendar = '.calendar-month:last-child';
    return {
      LeftArrows: '.month-left-arrow:not(.month-arrow-disabled)',
      LeftCalendarHeaderName: `${LeftCalendar} .month-header-name`,
      LeftCalendarLeftArrow: `${LeftCalendar} .month-left-arrow`,
      LeftCalendarRightArrow: `${LeftCalendar} .month-right-arrow`,
      RightArrows: '.month-right-arrow:not(.month-arrow-disabled)',
      RightCalendarHeaderName: `${RightCalendar} .month-header-name`,
      RightCalendarRightArrow: `${RightCalendar} .month-right-arrow`,
    };
  }

  static elements = {
    leftArrowElements: this.selectors.LeftArrows,
    rightArrowElements: this.selectors.RightArrows,
  };

  static tagName = 'calendar-control';

  #currentIndex = 0;
}

CalendarControl.defineCustomElement();

export { CalendarControl };
